import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FlatList,
  Keyboard,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { TonyteMapView as WebView, type TonyteMapViewRef } from '../components/TonyteMapView';
type WebViewMessageEvent = { nativeEvent: { data: string } };
import LinearGradient from 'react-native-linear-gradient';
import Icon from 'react-native-vector-icons/Feather';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import TonyteBackground from '../components/TonyteBackground';
import LoadingSpinner from '../components/LoadingSpinner';
import { auth } from '../firebase';
import { useProfile } from '../hooks/useProfile';
import { useStudiZoneEvents } from '../hooks/useStudiZoneEvents';
import { useCampusMaps } from '../hooks/useCampusMaps';
import { Radius, Typography } from '../theme';
import type { RootStackParamList, TonyteActivity } from '../types';
import { getResolvedEventId } from '../utils/eventIds';
import { ANALYTICS_SOURCES, trackEventDetailOpen } from '../utils/eventAnalytics';
import {
  findUniversityForProfile,
  matchActivityToCampus,
  searchUniversities,
  type CampusMatch,
  type CampusUniversity,
} from '../utils/campusMaps';
import { useI18n } from '../i18n';
import { useTheme } from '../theme/useTheme';
import type { TonyteTheme } from '../theme/themes';
import { CARTO_VOYAGER_TILE_URL } from '../config/cartoConfig';

const TILE_URL = CARTO_VOYAGER_TILE_URL;
const DEFAULT_CENTER = { lat: 51.0504, lng: 13.7373 };
const DEFAULT_ZOOM = 13;

function toFiniteNumber(value: unknown): number | null {
  const numberValue = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return Number.isFinite(numberValue) ? numberValue : null;
}

function getCoordinates(activity: TonyteActivity): { lat: number; lng: number } | null {
  const value = activity.coordinates;
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const lat = toFiniteNumber(raw.lat ?? raw.latitude);
  const lng = toFiniteNumber(raw.lng ?? raw.longitude ?? raw.lon);
  if (lat === null || lng === null) return null;
  return { lat, lng };
}

const toJs = (value: unknown) => JSON.stringify(value).replace(/</g, '\\u003c');

// A stable shell with no data baked in — universities, events and the
// active campus are pushed in via injectJavaScript once the WebView reports
// ready, so pan/zoom state survives refreshes and Leaflet loads once.
function buildStudentMapShellHtml(isDark: boolean) {
  const mapBackground = isDark ? '#040815' : '#F4EFE7';
  const popupBackground = isDark ? 'rgba(3,7,18,0.94)' : 'rgba(255,250,242,0.97)';
  const popupText = isDark ? '#ffffff' : '#211A16';
  const popupMuted = isDark ? 'rgba(224,242,254,0.72)' : '#74675D';
  const popupBorder = isDark ? 'rgba(186,230,253,0.2)' : 'rgba(118,95,72,0.24)';
  const pillBackground = isDark ? 'rgba(13,19,31,0.94)' : 'rgba(255,252,246,0.97)';
  const pillText = isDark ? '#ffffff' : '#181817';
  const pillBorder = isDark ? 'rgba(255,255,255,0.16)' : 'rgba(130,108,84,0.24)';
  return `<!doctype html>
<html>
<head>
  <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0">
  <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css">
  <script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script>
  <style>
    html, body, #map { width: 100%; height: 100%; margin: 0; background: ${mapBackground}; }
    .student-pin {
      width: 30px; height: 30px; border-radius: 15px;
      border: 2px solid #fff;
      background: linear-gradient(135deg, #26D0CE, #159B9A);
      box-shadow: 0 6px 18px rgba(21,155,154,0.35);
      display: grid; place-items: center;
      color: white; font: 800 12px -apple-system, BlinkMacSystemFont, sans-serif;
    }
    .campus-pill {
      display: inline-flex; align-items: center; gap: 6px; white-space: nowrap;
      padding: 6px 10px 6px 6px; border-radius: 999px;
      background: ${pillBackground}; color: ${pillText}; border: 1px solid ${pillBorder};
      box-shadow: 0 6px 18px rgba(0,0,0,0.18);
      font: 800 12px -apple-system, BlinkMacSystemFont, sans-serif;
      transform: translate(-50%, -50%);
    }
    .campus-pill b { display: grid; place-items: center; min-width: 22px; height: 22px; padding: 0 5px; border-radius: 11px; color: #fff; font-size: 10px; }
    .campus-pill span { font-weight: 600; opacity: 0.7; }
    .building-pin {
      display: inline-flex; align-items: center; gap: 5px; white-space: nowrap;
      padding: 4px 9px 4px 4px; border-radius: 999px;
      background: #181817; color: #fff; border: 2px solid #fff;
      box-shadow: 0 8px 20px rgba(0,0,0,0.3);
      font: 800 12px -apple-system, BlinkMacSystemFont, sans-serif;
      transform: translate(-50%, -100%) translateY(-10px);
    }
    .building-pin b { display: grid; place-items: center; min-width: 22px; height: 22px; padding: 0 5px; border-radius: 11px; background: #26D0CE; color: #04201F; font-size: 11px; }
    .building-pin.is-active { background: #26D0CE; color: #04201F; }
    .building-pin.is-active b { background: #04201F; color: #fff; }
    .campus-plan { transition: opacity 0.25s ease; }
    .campus-plan.dark { filter: invert(0.9) hue-rotate(180deg) saturate(0.85) brightness(0.92); }
    .leaflet-popup-content-wrapper, .leaflet-popup-tip {
      background: ${popupBackground}; color: ${popupText}; border: 1px solid ${popupBorder};
    }
    .popup-title { font: 800 13px -apple-system, BlinkMacSystemFont, sans-serif; margin-bottom: 3px; }
    .popup-location { font: 600 11px -apple-system, BlinkMacSystemFont, sans-serif; color: ${popupMuted}; }
  </style>
</head>
<body>
  <div id="map"></div>
  <script>
    function escapeHtml(value) {
      return String(value || '')
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }
    function post(message) {
      window.ReactNativeWebView && window.ReactNativeWebView.postMessage(JSON.stringify(message));
    }
    const isDark = ${isDark ? 'true' : 'false'};
    const map = L.map('map', { zoomControl: false, attributionControl: false, zoomSnap: 0.25 }).setView([${DEFAULT_CENTER.lat}, ${DEFAULT_CENTER.lng}], ${DEFAULT_ZOOM});
    L.tileLayer('${TILE_URL}', { maxZoom: 19 }).addTo(map);
    const eventsLayer = L.layerGroup().addTo(map);
    const campusBadgesLayer = L.layerGroup().addTo(map);
    const campusLayer = L.layerGroup().addTo(map);
    let campusMode = false;

    window.setStudiZoneEvents = function (events) {
      eventsLayer.clearLayers();
      events.forEach(event => {
        const icon = L.divIcon({ className: '', html: '<div class="student-pin">S</div>', iconSize: [30, 30], iconAnchor: [15, 15] });
        const marker = L.marker([event.lat, event.lng], { icon }).addTo(eventsLayer);
        marker.bindPopup('<div class="popup-title">' + escapeHtml(event.title) + '</div><div class="popup-location">' + escapeHtml(event.location) + '</div>');
        marker.on('click', () => post({ type: 'markerPress', id: event.id }));
      });
    };

    window.setUniversities = function (universities) {
      campusBadgesLayer.clearLayers();
      universities.forEach(university => {
        university.campuses.forEach(campus => {
          const label = university.campuses.length > 1 ? campus.name : university.name;
          const html = '<div class="campus-pill"><b style="background:' + escapeHtml(university.color) + '">' + escapeHtml(university.shortName) + '</b>' + escapeHtml(label) + (campus.count ? ' <span>· ' + campus.count + '</span>' : '') + '</div>';
          const marker = L.marker([campus.center.lat, campus.center.lng], { icon: L.divIcon({ className: '', html, iconSize: [0, 0] }) }).addTo(campusBadgesLayer);
          marker.on('click', () => post({ type: 'campusPress', universityId: university.id, campusId: campus.id }));
        });
      });
    };

    window.showCampus = function (campus) {
      campusMode = true;
      campusLayer.clearLayers();
      map.removeLayer(eventsLayer);
      map.removeLayer(campusBadgesLayer);
      if (campus.plan) {
        const b = campus.plan.bounds;
        const bounds = [[b.south, b.west], [b.north, b.east]];
        L.imageOverlay(campus.plan.url, bounds, { className: 'campus-plan' + (isDark ? ' dark' : ''), interactive: false }).addTo(campusLayer);
      }
      (campus.buildings || []).forEach(building => {
        const html = '<div class="building-pin' + (building.active ? ' is-active' : '') + '"><b>' + escapeHtml(building.code) + '</b>' + building.count + '</div>';
        const marker = L.marker([building.lat, building.lng], { icon: L.divIcon({ className: '', html, iconSize: [0, 0] }), zIndexOffset: building.active ? 1000 : 0 }).addTo(campusLayer);
        marker.on('click', () => post({ type: 'buildingPress', code: building.code }));
      });
      if (campus.fly) map.flyTo([campus.center.lat, campus.center.lng], campus.zoom, { duration: 0.8 });
    };

    window.showDresden = function () {
      const wasCampus = campusMode;
      campusMode = false;
      campusLayer.clearLayers();
      if (!map.hasLayer(eventsLayer)) eventsLayer.addTo(map);
      if (!map.hasLayer(campusBadgesLayer)) campusBadgesLayer.addTo(map);
      if (wasCampus) map.flyTo([${DEFAULT_CENTER.lat}, ${DEFAULT_CENTER.lng}], ${DEFAULT_ZOOM}, { duration: 0.8 });
    };

    setTimeout(() => post({ type: 'ready' }), 50);
  </script>
</body>
</html>`;
}

const StudiZoneMapScreen: React.FC = () => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const ui = useMemo(() => createMapStyles(theme), [theme]);
  const insets = useSafeAreaInsets();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { profile } = useProfile(auth.currentUser?.uid || null);
  const { studiZoneEvents, loading } = useStudiZoneEvents(profile);
  const { universities } = useCampusMaps();
  const mapHtml = useMemo(() => buildStudentMapShellHtml(theme.isDark), [theme.isDark]);
  const mapRef = useRef<TonyteMapViewRef>(null);
  const [mapReady, setMapReady] = useState(false);
  const [selection, setSelection] = useState<{ universityId: string; campusId: string } | null>(null);
  const [selectedBuilding, setSelectedBuilding] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const flyNextCampus = useRef(false);

  const ownUniversity = useMemo(() => findUniversityForProfile(universities, profile), [universities, profile]);
  // The student's own university first, then the configured order.
  const orderedUniversities = useMemo(() => {
    const list = searchUniversities(universities, '');
    return ownUniversity ? [ownUniversity, ...list.filter(u => u.id !== ownUniversity.id)] : list;
  }, [universities, ownUniversity]);
  const searchResults = useMemo(() => searchUniversities(universities, searchText), [universities, searchText]);

  const matches = useMemo(() => {
    const byId = new Map<string, CampusMatch>();
    studiZoneEvents.forEach(event => {
      const match = matchActivityToCampus(event, universities);
      if (match) byId.set(event.id, match);
    });
    return byId;
  }, [studiZoneEvents, universities]);

  const selectedUniversity = selection ? universities.find(u => u.id === selection.universityId) || null : null;
  const selectedCampus = selectedUniversity?.campuses.find(c => c.id === selection?.campusId) || null;

  const campusEvents = useMemo(() => {
    if (!selection) return [];
    return studiZoneEvents.filter(event => {
      const match = matches.get(event.id);
      return match?.universityId === selection.universityId && match.campusId === selection.campusId;
    });
  }, [studiZoneEvents, matches, selection]);

  const dockEvents = selection
    ? campusEvents.filter(event => !selectedBuilding || matches.get(event.id)?.building.code === selectedBuilding)
    : studiZoneEvents;

  const selectCampus = useCallback((universityId: string, campusId?: string) => {
    const university = universities.find(u => u.id === universityId);
    if (!university) return;
    flyNextCampus.current = true;
    setSelection({ universityId, campusId: campusId || university.campuses[0].id });
    setSelectedBuilding(null);
    setSearchText('');
    setSearchFocused(false);
    Keyboard.dismiss();
  }, [universities]);

  const backToDresden = useCallback(() => {
    setSelection(null);
    setSelectedBuilding(null);
  }, []);

  const openEvent = useCallback((activity: TonyteActivity) => {
    trackEventDetailOpen({ eventId: getResolvedEventId(activity), organizerId: activity.hostId, source: ANALYTICS_SOURCES.MAP });
    navigation.navigate('StudiZoneEventDetail', { eventId: getResolvedEventId(activity), event: activity });
  }, [navigation]);

  const handleMapMessage = useCallback((event: WebViewMessageEvent) => {
    try {
      const payload = JSON.parse(event.nativeEvent.data);
      if (payload.type === 'ready') {
        setMapReady(true);
      } else if (payload.type === 'markerPress') {
        const activity = studiZoneEvents.find(item => item.id === payload.id);
        if (activity) openEvent(activity);
      } else if (payload.type === 'campusPress') {
        selectCampus(payload.universityId, payload.campusId);
      } else if (payload.type === 'buildingPress') {
        setSelectedBuilding(current => (current === payload.code ? null : payload.code));
      }
    } catch {
      // Ignore malformed webview messages.
    }
  }, [studiZoneEvents, openEvent, selectCampus]);

  // Dresden layer: every StudiZone event with a position. Events matched to
  // a campus building but without their own coordinates get the building's.
  useEffect(() => {
    if (!mapReady) return;
    const markers = studiZoneEvents
      .map(event => {
        const coords = getCoordinates(event) || matches.get(event.id)?.building.pin || null;
        return coords ? { id: event.id, title: event.title, location: event.locationName || event.location || 'Campus', lat: coords.lat, lng: coords.lng } : null;
      })
      .filter(Boolean);
    mapRef.current?.injectJavaScript(`window.setStudiZoneEvents && window.setStudiZoneEvents(${toJs(markers)}); true;`);
  }, [mapReady, studiZoneEvents, matches]);

  useEffect(() => {
    if (!mapReady) return;
    const counts = new Map<string, number>();
    matches.forEach(match => {
      const key = `${match.universityId}/${match.campusId}`;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    const payload = universities.map((university: CampusUniversity) => ({
      id: university.id,
      name: university.name,
      shortName: university.shortName,
      color: university.accentColor,
      campuses: university.campuses.map(campus => ({
        id: campus.id,
        name: campus.name,
        center: campus.center,
        count: counts.get(`${university.id}/${campus.id}`) || 0,
      })),
    }));
    mapRef.current?.injectJavaScript(`window.setUniversities && window.setUniversities(${toJs(payload)}); true;`);
  }, [mapReady, universities, matches]);

  useEffect(() => {
    if (!mapReady) return;
    if (!selectedCampus) {
      mapRef.current?.injectJavaScript('window.showDresden && window.showDresden(); true;');
      return;
    }
    const perBuilding = new Map<string, number>();
    campusEvents.forEach(event => {
      const code = matches.get(event.id)?.building.code;
      if (code) perBuilding.set(code, (perBuilding.get(code) || 0) + 1);
    });
    const payload = {
      center: selectedCampus.center,
      zoom: selectedCampus.zoom,
      fly: flyNextCampus.current,
      plan: selectedCampus.plan ? { url: selectedCampus.plan.imageUrl, bounds: selectedCampus.plan.bounds } : null,
      buildings: selectedCampus.buildings
        .filter(building => perBuilding.has(building.code))
        .map(building => ({
          code: building.code,
          lat: building.pin.lat,
          lng: building.pin.lng,
          count: perBuilding.get(building.code),
          active: building.code === selectedBuilding,
        })),
    };
    flyNextCampus.current = false;
    mapRef.current?.injectJavaScript(`window.showCampus && window.showCampus(${toJs(payload)}); true;`);
  }, [mapReady, selectedCampus, campusEvents, matches, selectedBuilding]);

  const renderEventCard = useCallback(({ item }: { item: TonyteActivity }) => {
    const match = matches.get(item.id);
    return (
      <Pressable style={[styles.eventCard, ui.surface]} onPress={() => openEvent(item)}>
        <View style={[styles.cardIcon, ui.accentSoft]}>
          {match ? (
            <Text style={[styles.cardCode, ui.accentText]}>{match.building.code}</Text>
          ) : (
            <Icon name="award" size={15} color={theme.colors.accentSecondary} />
          )}
        </View>
        <View style={styles.cardCopy}>
          <Text style={[styles.cardTitle, ui.textPrimary]} numberOfLines={1}>{item.title}</Text>
          <View style={styles.cardMetaRow}>
            <Icon name="map-pin" size={11} color={theme.colors.iconMuted} />
            <Text style={[styles.cardMeta, ui.textSecondary]} numberOfLines={1}>{match?.building.name || item.locationName || item.location || 'Campus'}</Text>
          </View>
          <View style={styles.cardMetaRow}>
            <Icon name="calendar" size={11} color={theme.colors.accentSecondary} />
            <Text style={[styles.cardMeta, ui.textSecondary]} numberOfLines={1}>{[item.startDate, item.startTime].filter(Boolean).join(' · ') || t('studiZone.timeTba')}</Text>
          </View>
        </View>
        <View style={[styles.cardArrow, ui.accentSoft]}><Icon name="chevron-right" size={15} color={theme.colors.accentSecondary} /></View>
      </Pressable>
    );
  }, [matches, openEvent, t, theme.colors.accentSecondary, theme.colors.iconMuted, ui]);

  if (loading && !studiZoneEvents.length) {
    return <LoadingSpinner label={t('studiZone.loadingMap')} />;
  }

  const showResults = searchFocused && searchText.trim().length > 0;
  const subtitle = selectedUniversity && selectedCampus
    ? `${selectedUniversity.name} · ${selectedCampus.name}`
    : t('studiZone.allEvents');
  const dockCountLabel = selection
    ? t('studiZone.campusEventsCount').replace('{count}', String(campusEvents.length))
    : `${studiZoneEvents.length} ${t('studiZone.pins')}`;

  return (
    <View style={[styles.container, ui.container]}>
      <TonyteBackground appearanceAware style={StyleSheet.absoluteFill} />
      {/* TS ref objects are invariant, so the minimal shared TonyteMapViewRef
          needs a narrow cast on the ref prop (same as MapScreen.tsx). */}
      <WebView
        ref={mapRef as any}
        style={StyleSheet.absoluteFill}
        source={{ html: mapHtml, baseUrl: 'https://tonyte.app' }}
        originWhitelist={['*']}
        javaScriptEnabled
        domStorageEnabled
        onMessage={handleMapMessage}
        androidLayerType="hardware"
        mixedContentMode="always"
        setSupportMultipleWindows={false}
      />

      <LinearGradient
        pointerEvents="none"
        colors={theme.isDark ? ['rgba(4,8,21,0.94)', 'rgba(4,8,21,0.55)', 'rgba(4,8,21,0)'] : ['rgba(245,240,232,0.97)', 'rgba(245,240,232,0.7)', 'rgba(245,240,232,0)']}
        style={[styles.topShade, { height: insets.top + 210 }]}
      />

      <View style={[styles.header, ui.headerPanel, { paddingTop: insets.top + 10 }]}> 
        <View style={styles.headerRow}>
          <TouchableOpacity
            style={[styles.iconButton, ui.surface]}
            onPress={() => (selection ? backToDresden() : navigation.goBack())}
            accessibilityRole="button"
          >
            <Icon name="chevron-left" size={22} color={theme.colors.icon} />
          </TouchableOpacity>
          <View style={styles.headerCopy}>
            <Text style={[styles.eyebrow, ui.accentText]}>{t('studiZone.verifiedLayer')}</Text>
            <Text style={[styles.title, ui.textPrimary]} numberOfLines={1}>{t('studiZone.mapTitle')}</Text>
            <Text style={[styles.subtitle, ui.textSecondary]} numberOfLines={1}>{subtitle}</Text>
          </View>
        </View>

        {universities.length ? (
          <>
            <View style={[styles.searchBar, ui.surface]}>
              <Icon name="search" size={16} color={theme.colors.iconMuted} />
              <TextInput
                value={searchText}
                onChangeText={setSearchText}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                placeholder={t('studiZone.searchUniversities')}
                placeholderTextColor={theme.colors.textPlaceholder}
                style={[styles.searchInput, ui.textPrimary]}
                returnKeyType="search"
                autoCorrect={false}
                onSubmitEditing={() => searchResults[0] && selectCampus(searchResults[0].id)}
              />
              {searchText ? (
                <TouchableOpacity onPress={() => setSearchText('')} hitSlop={10}>
                  <Icon name="x" size={16} color={theme.colors.iconMuted} />
                </TouchableOpacity>
              ) : null}
            </View>

            {showResults ? (
              <View style={[styles.results, ui.surface]}>
                {searchResults.length ? searchResults.slice(0, 6).map(university => (
                  <TouchableOpacity key={`result-${university.id}`} style={styles.resultRow} onPress={() => selectCampus(university.id)}>
                    <View style={[styles.uniBadge, { backgroundColor: university.accentColor }]}>
                      <Text style={styles.uniBadgeText} numberOfLines={1}>{university.shortName}</Text>
                    </View>
                    <View style={styles.resultCopy}>
                      <Text style={[styles.resultTitle, ui.textPrimary]} numberOfLines={1}>{university.name}</Text>
                      <Text style={[styles.resultSub, ui.textSecondary]} numberOfLines={1}>
                        {university.campuses.length > 1
                          ? t('studiZone.campusesCount').replace('{count}', String(university.campuses.length))
                          : university.fullName}
                      </Text>
                    </View>
                    <Icon name="chevron-right" size={16} color={theme.colors.iconMuted} />
                  </TouchableOpacity>
                )) : (
                  <Text style={[styles.noResults, ui.textSecondary]}>{t('studiZone.noUniversityResults')}</Text>
                )}
              </View>
            ) : (
              <>
                <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.chipRow} keyboardShouldPersistTaps="handled">
                  <TouchableOpacity style={[styles.chip, !selection ? ui.chipActive : ui.surface]} onPress={backToDresden}>
                    <Icon name="map" size={13} color={!selection ? ui.chipActiveText.color : theme.colors.iconSubtle} />
                    <Text style={[styles.chipText, !selection ? ui.chipActiveText : ui.textSecondary]}>{t('studiZone.dresden')}</Text>
                  </TouchableOpacity>
                  {orderedUniversities.map(university => {
                    const active = selection?.universityId === university.id;
                    return (
                      <TouchableOpacity key={`uni-${university.id}`} style={[styles.chip, active ? ui.chipActive : ui.surface]} onPress={() => selectCampus(university.id)}>
                        <View style={[styles.chipDot, { backgroundColor: university.accentColor }]} />
                        <Text style={[styles.chipText, active ? ui.chipActiveText : ui.textSecondary]}>{university.name}</Text>
                        {ownUniversity?.id === university.id ? (
                          <Text style={[styles.chipTag, active ? ui.chipActiveText : ui.accentText]}>{t('studiZone.yourUniversity')}</Text>
                        ) : null}
                      </TouchableOpacity>
                    );
                  })}
                </ScrollView>
                {selectedUniversity && selectedUniversity.campuses.length > 1 ? (
                  <View style={[styles.segment, ui.surface]}>
                    {selectedUniversity.campuses.map(campus => {
                      const active = campus.id === selection?.campusId;
                      return (
                        <TouchableOpacity key={`campus-${campus.id}`} style={[styles.segmentItem, active && ui.accentSoft]} onPress={() => selectCampus(selectedUniversity.id, campus.id)}>
                          <Text style={[styles.segmentText, active ? ui.accentText : ui.textSecondary]} numberOfLines={1}>{campus.name}</Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                ) : null}
              </>
            )}
          </>
        ) : null}
      </View>

      <View style={[styles.cardDock, ui.dock, { paddingBottom: insets.bottom + 14 }]} pointerEvents="box-none">
        <View style={styles.sheetHandle} />
        <View style={styles.dockHeader} pointerEvents="box-none">
          <View style={styles.dockTitleWrap}>
            <Text style={[styles.dockTitle, ui.textPrimary]}>{selection ? t('studiZone.campusEvents') : t('studiZone.allEvents')}</Text>
            <Text style={[styles.dockSubtitle, ui.textSecondary]}>{dockCountLabel}</Text>
          </View>
          {selectedBuilding && selectedCampus ? (
            <TouchableOpacity style={[styles.buildingFilter, ui.chipActive]} onPress={() => setSelectedBuilding(null)}>
              <Text style={[styles.dockPillText, ui.chipActiveText]} numberOfLines={1}>
                {selectedBuilding} · {selectedCampus.buildings.find(b => b.code === selectedBuilding)?.name}
              </Text>
              <Icon name="x" size={13} color={ui.chipActiveText.color} />
            </TouchableOpacity>
          ) : null}
        </View>
        {selection && !campusEvents.length ? (
          <View style={[styles.emptyCard, ui.surface]}>
            <Text style={[styles.cardTitle, ui.textPrimary]}>{t('studiZone.noCampusEvents')}</Text>
            <Text style={[styles.cardMeta, ui.textSecondary]}>{t('studiZone.noCampusEventsSub')}</Text>
          </View>
        ) : (
          <FlatList
            data={dockEvents}
            horizontal
            keyExtractor={item => `event-${item.id}`}
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.cardList}
            renderItem={renderEventCard}
            initialNumToRender={8}
            windowSize={5}
            removeClippedSubviews
          />
        )}
        {selectedCampus?.plan?.attribution ? (
          <Text style={[styles.attribution, ui.textMuted]}>{selectedCampus.plan.attribution}</Text>
        ) : null}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  topShade: { position: 'absolute', top: 0, left: 0, right: 0 },
  header: { position: 'absolute', top: 0, left: 0, right: 0, paddingHorizontal: 16, paddingBottom: 14, gap: 10, borderBottomWidth: 1 },
  headerRow: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  iconButton: { width: 42, height: 42, borderRadius: 14, borderWidth: 1, alignItems: 'center', justifyContent: 'center' },
  headerCopy: { flex: 1, minWidth: 0 },
  eyebrow: { marginBottom: 1, fontSize: 9, lineHeight: 11, fontWeight: Typography.extrabold, letterSpacing: 1.4, textTransform: 'uppercase' },
  title: { fontSize: 22, lineHeight: 26, fontWeight: Typography.extrabold, letterSpacing: -0.35 },
  subtitle: { marginTop: 1, fontSize: 11, fontWeight: Typography.bold },
  searchBar: { height: 44, borderRadius: 14, borderWidth: 1, paddingHorizontal: 13, flexDirection: 'row', alignItems: 'center', gap: 10 },
  searchInput: { flex: 1, fontSize: 15, fontWeight: Typography.semibold, paddingVertical: 0 },
  results: { borderRadius: 16, borderWidth: 1, paddingVertical: 6, overflow: 'hidden' },
  resultRow: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 12, paddingVertical: 10 },
  resultCopy: { flex: 1, minWidth: 0 },
  resultTitle: { fontSize: 14, fontWeight: Typography.extrabold },
  resultSub: { marginTop: 2, fontSize: 12, fontWeight: Typography.semibold },
  noResults: { paddingHorizontal: 14, paddingVertical: 12, fontSize: 13, fontWeight: Typography.semibold },
  uniBadge: { minWidth: 36, height: 36, borderRadius: 12, paddingHorizontal: 6, alignItems: 'center', justifyContent: 'center' },
  uniBadgeText: { color: '#fff', fontSize: 11, fontWeight: Typography.extrabold },
  chipRow: { gap: 8, paddingRight: 16 },
  chip: { borderRadius: Radius.pill, borderWidth: 1, paddingHorizontal: 12, paddingVertical: 8, flexDirection: 'row', alignItems: 'center', gap: 6 },
  chipDot: { width: 8, height: 8, borderRadius: 4 },
  chipText: { fontSize: 13, fontWeight: Typography.bold },
  chipTag: { fontSize: 10, fontWeight: Typography.extrabold, textTransform: 'uppercase', letterSpacing: 0.6 },
  segment: { alignSelf: 'flex-start', borderRadius: Radius.pill, borderWidth: 1, padding: 4, flexDirection: 'row', gap: 4 },
  segmentItem: { borderRadius: Radius.pill, paddingHorizontal: 12, paddingVertical: 6 },
  segmentText: { fontSize: 12, fontWeight: Typography.extrabold },
  cardDock: { position: 'absolute', left: 0, right: 0, bottom: 0, gap: 10, paddingTop: 7, borderTopLeftRadius: 24, borderTopRightRadius: 24, borderWidth: 1, borderBottomWidth: 0 },
  sheetHandle: { alignSelf: 'center', width: 38, height: 4, borderRadius: 2, backgroundColor: 'rgba(148,163,184,0.42)', marginBottom: 1 },
  dockHeader: { minHeight: 42, paddingHorizontal: 16, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10 },
  dockTitleWrap: { flex: 1, minWidth: 0 },
  dockTitle: { fontSize: 15, fontWeight: Typography.extrabold },
  dockSubtitle: { marginTop: 2, fontSize: 11, fontWeight: Typography.semibold },
  dockPill: { maxWidth: '75%', borderRadius: Radius.pill, borderWidth: 1, paddingHorizontal: 12, paddingVertical: 6, flexDirection: 'row', alignItems: 'center', gap: 6 },
  dockPillText: { flexShrink: 1, fontSize: 12, fontWeight: Typography.extrabold },
  buildingFilter: { maxWidth: '55%', borderRadius: Radius.pill, paddingHorizontal: 10, paddingVertical: 7, flexDirection: 'row', alignItems: 'center', gap: 5 },
  cardList: { paddingHorizontal: 16, gap: 10 },
  eventCard: { width: 278, minHeight: 96, borderRadius: 16, borderWidth: 1, padding: 12, flexDirection: 'row', alignItems: 'center', gap: 11 },
  emptyCard: { marginHorizontal: 16, borderRadius: 22, borderWidth: 1, padding: 14, gap: 4 },
  cardIcon: { width: 42, height: 42, borderRadius: 14, alignItems: 'center', justifyContent: 'center' },
  cardCode: { fontSize: 15, fontWeight: Typography.extrabold },
  cardCopy: { flex: 1, minWidth: 0, gap: 4 },
  cardTitle: { fontSize: Typography.sm, fontWeight: Typography.extrabold },
  cardMetaRow: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  cardMeta: { flex: 1, fontSize: 10, fontWeight: Typography.bold },
  cardArrow: { width: 28, height: 28, borderRadius: 10, alignItems: 'center', justifyContent: 'center' },
  attribution: { paddingHorizontal: 18, fontSize: 9, fontWeight: Typography.semibold },
});

// Opaque, theme-driven surfaces (matches the StudiZone feed) so controls
// stay readable over both the basemap and the campus plan.
const createMapStyles = (theme: TonyteTheme) => {
  const dark = theme.isDark;
  return StyleSheet.create({
    container: { backgroundColor: theme.colors.background },
    headerPanel: {
      backgroundColor: dark ? 'rgba(7,14,25,0.94)' : 'rgba(255,252,246,0.95)',
      borderColor: theme.colors.border,
      ...theme.shadows.navigation,
    },
    dock: {
      backgroundColor: dark ? 'rgba(7,14,25,0.97)' : 'rgba(255,252,246,0.98)',
      borderColor: theme.colors.border,
      ...theme.shadows.navigation,
    },
    textPrimary: { color: theme.colors.textPrimary },
    textSecondary: { color: theme.colors.textSecondary },
    textMuted: { color: theme.colors.textMuted },
    accentText: { color: theme.colors.accentSecondary },
    accentSoft: { backgroundColor: dark ? 'rgba(38,208,206,0.14)' : 'rgba(21,155,154,0.12)' },
    surface: {
      backgroundColor: dark ? '#111826' : theme.colors.surfaceElevated,
      borderColor: theme.colors.border,
      ...theme.shadows.card,
    },
    chipActive: { backgroundColor: theme.colors.accentSecondary, borderColor: theme.colors.accentSecondary },
    chipActiveText: { color: dark ? '#04201F' : '#FFFFFF' },
  });
};

export default StudiZoneMapScreen;
